// Reading a project task's plan (api/utils/tasks/schedule.ts works it out;
// the browser only displays it). People set start_date / due_date /
// duration_days / predecessors; the scheduler sets plan_start / plan_finish /
// slack_days / conflict_days / critical.
import { Predecessor, TaskItem } from '../types/tasks';
import { daysBetween, parseDay } from './taskDates';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const isScheduled = (item: TaskItem) => !!item.plan_start && !!item.plan_finish;
const open = (item: TaskItem) => item.progress < 100;

/** Milestones and events mark points in the plan; only work can be late. */
const isWork = (item: TaskItem) => item.node_kind !== 'milestone' && item.node_kind !== 'event';

/**
 * On the critical path: the single longest chain of linked topics, buffer days
 * included (api/utils/tasks/schedule.ts). Events and milestones on it count.
 * Not the same as late — a late task off the chain isn't critical, and a
 * critical one can be on time.
 */
export const isCritical = (item: TaskItem) => open(item) && !!item.critical;
/** Can't be done in time as planned — misses a deadline, an event, a dated milestone. */
export const isLate = (item: TaskItem) => open(item) && isWork(item) && item.slack_days != null && item.slack_days < 0;
/** Pinned to start before what it waits for allows. */
export const hasConflict = (item: TaskItem) => open(item) && (item.conflict_days ?? 0) > 0;

/** CSS state classes, independent of each other: late and critical are separate states. */
export const planClasses = (item: TaskItem) =>
  [isCritical(item) && 'is-critical', isLate(item) && 'is-late', hasConflict(item) && 'is-conflict'].filter(Boolean).join(' ');

/** Planned length in days (a milestone is 0). */
export function planDays(item: TaskItem): number {
  if (!isScheduled(item) || item.node_kind === 'milestone') return 0;
  return daysBetween(item.plan_start!, item.plan_finish!) + 1;
}

/** 'Nov 3', 'Nov 3–7', 'Nov 28 – Dec 2' */
export function rangeLabel(from: string, to: string): string {
  const a = parseDay(from);
  const b = parseDay(to);
  if (from === to) return `${MONTHS[a.getMonth()]} ${a.getDate()}`;
  if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()) {
    return `${MONTHS[a.getMonth()]} ${a.getDate()}–${b.getDate()}`;
  }
  return `${MONTHS[a.getMonth()]} ${a.getDate()} – ${MONTHS[b.getMonth()]} ${b.getDate()}`;
}

export const DEP_LABELS: Record<Predecessor['type'], string> = {
  FS: 'Starts after it finishes',
  SS: 'Starts when it starts',
  FF: 'Finishes when it finishes',
  SF: 'Finishes when it starts',
};

/** The dates people set on it, as the Schedule panel shows them. */
export function hasOwnSchedule(item: TaskItem) {
  return !!(item.start_date || item.due_date || item.duration_days || (item.predecessors || []).length);
}

/**
 * An event that waits for other work floats: the scheduler starts it once
 * that's done and ignores any dates it was given (api/utils/tasks/schedule.ts).
 */
export const floats = (item: TaskItem) => item.node_kind === 'event' && (item.predecessors || []).length > 0;
